/**
 * Public PDF invoice viewer.
 * Fetches the invoice via the public API and renders it using @react-pdf/renderer
 * in an embedded iframe so the user can download or print it.
 */
import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Loader2, FileText } from 'lucide-react';
import { PDFViewer } from '@react-pdf/renderer';
import { type PdfCompanyInfo } from '@/components/documents/invoices/InvoicePDF';
import { InvoiceDocumentPdf } from '@/components/documents/invoices/InvoiceDocumentPdf';
import { publicGet } from '@/lib/api/publicClient';
import { formatAddress } from '@/lib/format-address';
import { DocumentNotFound } from '@/components/public/DocumentNotFound';
import { AutoPrintPdf } from '@/components/documents/AutoPrintPdf';
import type { PublicInvoice } from '@/types/public';
// `toInvoice` lives in a shared lib so the hosted pay page renders the design
// document from the SAME public→Invoice adapter this PDF uses.
import { toInvoice } from '@/lib/invoices/publicInvoiceToInvoice';

export const Route = createFileRoute('/(public)/pdf-invoice/$id')({
  // `?print=1` (opened by the portal's Print action) auto-prints the document.
  validateSearch: (search: Record<string, unknown>) => ({
    print: search.print === '1' || search.print === true || search.print === 1,
  }),
  component: PdfInvoicePage,
});

function PdfInvoicePage() {
  const { id } = Route.useParams();
  const { print } = Route.useSearch();

  const {
    data: pub,
    isLoading,
    isError,
  } = useQuery<PublicInvoice>({
    queryKey: ['public', 'invoice', id],
    queryFn: () => publicGet<PublicInvoice>(`/api/public/invoices/${id}`),
    retry: false,
  });

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center gap-2 text-gray-500">
        <Loader2 className="h-5 w-5 animate-spin" />
        <span className="text-sm">Loading invoice…</span>
      </div>
    );
  }

  if (isError || !pub) {
    return <DocumentNotFound message="Invoice not found" />;
  }

  const invoice = toInvoice(pub);
  const company: PdfCompanyInfo = {
    name: pub.business?.name ?? 'Your Company',
    logoUrl: pub.business?.logo ?? null,
    primaryColor: pub.business?.primaryColor ?? null,
    invoiceFooterText: pub.business?.invoiceFooterText ?? null,
    address: formatAddress(pub.business?.address) ?? null,
    email: pub.business?.email ?? null,
    phone: pub.business?.phone ?? null,
    website: pub.business?.website ?? null,
    // NOT-7 — propagate the company's tax registration number to the PDF so
    // it prints under the company header on the customer-facing invoice.
    taxRegistrationNumber: pub.business?.taxRegistrationNumber ?? null,
    contractorLicense: pub.business?.contractorLicense ?? null,
    regulatoryNotice: pub.business?.regulatoryNotice ?? null,
  };
  // The public invoice payload carries no payment ledger, so drop the
  // paymentHistory block from the customer-facing templated PDF — otherwise it
  // is a section that can never populate here (the office PDF still shows it).
  const publicLayout = pub.resolvedDocLayout
    ? {
        ...pub.resolvedDocLayout,
        blocks: (pub.resolvedDocLayout.blocks ?? []).filter((b) => b.type !== 'paymentHistory'),
      }
    : null;
  const doc = (
    <InvoiceDocumentPdf
      invoice={invoice}
      company={company}
      wisetack={pub.wisetack ?? null}
      promotionName={pub.promotionName}
      // Customer-facing dates render in the company's timezone (SPEC-tz-6).
      timeZone={pub.timezone ?? null}
      resolvedDocLayout={publicLayout}
    />
  );

  return (
    <div className="flex min-h-screen flex-col">
      {/* Minimal header */}
      <div className="flex h-12 items-center gap-2 border-b bg-white px-4 shadow-sm">
        <FileText className="h-4 w-4 text-gray-500" />
        <span className="text-sm font-medium text-gray-700">
          {invoice.invoiceNumber ? `Invoice #${invoice.invoiceNumber}` : 'Invoice'}
        </span>
        {pub.business?.name && <span className="text-sm text-gray-400">· {pub.business.name}</span>}
      </div>

      {/* PDF viewer fills remaining viewport */}
      <div className="flex-1">
        <PDFViewer
          width="100%"
          height="100%"
          style={{ border: 'none', minHeight: 'calc(100vh - 48px)' }}
        >
          {doc}
        </PDFViewer>
      </div>
      {print ? <AutoPrintPdf document={doc} /> : null}
    </div>
  );
}
